/**
 * Payroll hours: everyone's hours for one pay period, and the files the
 * office feeds to payroll.
 *
 * One read (GET /v1/payroll/hours) gives the period, every line the
 * ledger holds for every person (case-note hours with the client and
 * band, internal time with its kind) and the per-person summary. The
 * header pages through periods. The preview is the payroll file itself,
 * grouped by person (LP-39): a heading row with the name and the person's
 * total, then their paid lines as Date, Hours, Rate, Client or internal,
 * from the same payrollFilePeople the file is written from. The totals
 * are the screen's own: the file carries one row per line and no total
 * row. The screen shows dates in the period label's short form ("Mon 2
 * Nov"); the file keeps YYYY-MM-DD. Every line shows: nothing folds and
 * nothing pages.
 * Download for payroll writes that file; Download full
 * detail writes every ledger line in the twenty columns the Payroll
 * Project's overtime and retro rules read. Hours only, never a rate or
 * an amount: the pay math lives in the Payroll Project the files feed.
 *
 * Gate: export_payroll_hours (the owner and the payroll seat). The
 * server also requires view_all_clinical, because the ledger's note side
 * runs under the care-team policy and a narrower seat would get a
 * shorter file that looks complete.
 */

import { type ReactNode, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BackToInvoices } from '@/components/BackToInvoices';
import { Button } from '@/components/primitives/Button';
import { PageShell } from '@/components/primitives/PageShell';
import {
  PAYROLL_CSV_HEADER,
  buildPayrollCsv,
  buildPayrollDetailCsv,
  payrollCsvFilename,
  payrollDetailCsvFilename,
  payrollFilePeople,
} from '@/components/payroll/payrollCsv';
import { useTenant } from '@/lib/auth';
import { canExportPayrollHours } from '@/lib/capabilities';
import { addDays } from '@/lib/capacity';
import { capitalizeFirst } from '@/lib/capitalizeFirst';
import { downloadCsvFile } from '@/lib/csv';
import { sumHours } from '@/lib/hours';
import { usePayrollHours, type PayrollPeriodWire } from '@/lib/payrollApi';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { contactSentence, perVisitPayText } from '@/lib/errorCopy';
import { readRateBands } from '@/lib/notes';
import { periodDayLabel } from '@/lib/payPeriod';

const PERIOD_PARAM = 'period';

const TABLE_HEAD_ROW = 'text-left text-label font-semibold uppercase tracking-label text-sage-400';

/** The file's columns without Name: the name heads each person's group. */
const LINE_COLUMNS = PAYROLL_CSV_HEADER.filter((h) => h !== 'Name');

/** After a total: the long-distance visits it leaves out. A SPICE Travel
 *  line is one unit of a pay code, not time worked (LP-46), so the hours
 *  total never counts it and this says how many there are. */
function TravelVisits({ count, testId }: { count: number; testId: string }): ReactNode {
  if (count === 0) return null;
  return <span data-testid={testId}>+ {count === 1 ? '1 long-distance visit' : `${count} long-distance visits`}</span>;
}

/** "10 lines from Mon 5 Oct to Fri 9 Oct", or "on Fri 9 Oct" for one day. */
function prePeriodSpan(count: number, from: string, to: string): string {
  const lines = count === 1 ? '1 line' : `${count} lines`;
  return from === to
    ? `${lines} on ${periodDayLabel(from)}`
    : `${lines} from ${periodDayLabel(from)} to ${periodDayLabel(to)}`;
}

const DAY_MS = 86_400_000;

function utcDay(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

/** The start of the period before this one: the same length, counted back. */
export function previousPeriodStart(period: PayrollPeriodWire): string {
  const lengthDays = Math.round((utcDay(period.end) - utcDay(period.start)) / DAY_MS) + 1;
  return addDays(period.start, -lengthDays);
}

export function PayrollHours(): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const [params, setParams] = useSearchParams();

  const allowed = canExportPayrollHours(whoami);
  const periodParam = params.get(PERIOD_PARAM);
  const { data, isLoading, isError, error, notConfigured, perVisitRefusal, refetch } = usePayrollHours(tenantSlug, periodParam, {
    enabled: allowed,
  });
  const contact = useSupportContact();
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const bands = readRateBands(tenantConfig?.config);
  const perVisitSentence = perVisitPayText(perVisitRefusal, (band) => bands.find((b) => b.value === band)?.label ?? band, contact);

  function goTo(date: string): void {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(PERIOD_PARAM, date);
      return next;
    });
  }

  const period = data?.period ?? null;
  const rows = data?.rows ?? [];
  const people = useMemo(() => (data === null ? [] : payrollFilePeople(data)), [data]);
  // Lines from earlier in the workweek than the period start: paid in the
  // period they fall in, so only the full detail file carries them. A
  // time-only line is never paid anywhere and is not counted.
  const prePeriodCount = rows.filter((r) => !r.in_period && r.kind !== 'per_visit_time').length;
  const total = useMemo(() => sumHours(people.map((p) => p.total)), [people]);
  const travelVisits = useMemo(() => people.reduce((n, p) => n + p.travelVisits, 0), [people]);

  function download(): void {
    if (data === null) return;
    downloadCsvFile(payrollCsvFilename(data.period), buildPayrollCsv(data));
  }

  function downloadDetail(): void {
    if (data === null) return;
    downloadCsvFile(payrollDetailCsvFilename(data.period), buildPayrollDetailCsv(data));
  }

  return (
    <PageShell
      title="Payroll hours"
      maxWidth="xl"
      access={{ allowed }}
      async={{ what: 'the period’s hours', isLoading, isError, error, onRetry: refetch }}
      testId="payroll-hours"
    >
      <BackToInvoices tenantSlug={tenantSlug} />

      {perVisitSentence !== null && (
        <p role="alert" className="rounded-card border border-sage-200 bg-sage-50 px-4 py-4 text-sm text-ink" data-testid="payroll-per-visit-refusal">
          {perVisitSentence}
        </p>
      )}

      {notConfigured && (
        <div
          className="rounded-card border border-sage-200 bg-sage-50 px-4 py-4 text-sm text-ink"
          data-testid="payroll-not-configured"
        >
          The pay-period calendar isn't set up yet. {capitalizeFirst(contactSentence('{contact_admin} can set it.', contact))}
        </div>
      )}

      {period !== null && data !== null && (
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-center gap-2" data-testid="period-header">
            <Button
              variant="ghost"
              size="sm"
              aria-label="Previous period"
              data-testid="period-prev"
              onClick={() => goTo(previousPeriodStart(period))}
            >
              ‹
            </Button>
            <p className="text-center font-display text-lg text-sage-800 sm:text-xl" data-testid="period-label">
              {period.label}
            </p>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Next period"
              data-testid="period-next"
              onClick={() => goTo(addDays(period.end, 1))}
            >
              ›
            </Button>
          </div>

          <section aria-labelledby="payroll-preview-heading">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 id="payroll-preview-heading" className="font-display text-xl text-sage-800">
                Hours by person
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" data-testid="payroll-download" onClick={download}>
                  Download for payroll
                </Button>
                <Button variant="secondary" size="sm" data-testid="payroll-download-detail" onClick={downloadDetail}>
                  Download full detail
                </Button>
              </div>
            </div>

            {prePeriodCount > 0 && data !== null && (
              <p className="mb-3 text-sm text-ink-soft" data-testid="payroll-pre-period-note">
                The full detail file also carries{' '}
                {prePeriodSpan(prePeriodCount, data.window_start, addDays(data.period.start, -1))}, the start of the workweek.
              </p>
            )}

            {people.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="payroll-empty">
                Once notes with hours post for this period, you'll see them here.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-card border border-sage-200 bg-paper px-4 py-2 shadow-sh1">
                <table className="w-full text-sm" data-testid="payroll-table">
                  <thead>
                    <tr className={TABLE_HEAD_ROW}>
                      {LINE_COLUMNS.map((h) => (
                        // The last column takes the spare width, so Hours
                        // sits beside the names and dates it totals.
                        <th
                          key={h}
                          className={`py-1.5 font-semibold${h === 'Hours' ? ' pr-6 text-right' : ' pr-3'}${h === 'Client or internal' ? ' w-full' : ''}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {people.map((p) => (
                    <tbody key={p.staffUserId} className="border-t border-sage-300" data-testid="payroll-person">
                      <tr data-testid="payroll-person-total">
                        <th
                          scope="rowgroup"
                          className="whitespace-nowrap pb-1 pr-3 pt-4 text-left text-value font-medium text-ink"
                          data-testid="total-name"
                        >
                          {p.name}
                        </th>
                        <td className="pb-1 pl-6 pr-6 pt-4 text-right font-medium tabular-nums text-ink">
                          <span className="sr-only">Total </span>
                          <span data-testid="total-hours">{p.total}</span>
                        </td>
                        <td colSpan={2} className="pb-1 pt-4 text-xs text-ink-soft">
                          <TravelVisits count={p.travelVisits} testId="total-travel" />
                        </td>
                      </tr>
                      {p.lines.map((l, i) => (
                        <tr key={i} className="border-t border-sage-100" data-testid="payroll-line">
                          <td className="whitespace-nowrap py-2 pr-3 tabular-nums text-ink-soft" data-testid="line-date">
                            {periodDayLabel(l.date)}
                          </td>
                          <td className="py-2 pl-6 pr-6 text-right tabular-nums" data-testid="line-hours">
                            {l.hours}
                          </td>
                          <td className="whitespace-nowrap py-2 pr-3 text-ink-soft" data-testid="line-rate">
                            {l.rate}
                          </td>
                          <td className="whitespace-nowrap py-2 pr-3" data-testid="line-client">
                            {l.clientOrInternal}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                  <tfoot>
                    <tr className="border-t border-sage-300">
                      <th scope="row" className="py-3 pr-3 text-left text-value font-medium text-ink">
                        {people.length === 1 ? '1 person' : `${people.length} people`}
                      </th>
                      <td className="py-3 pl-6 pr-6 text-right font-medium tabular-nums">
                        <span className="sr-only">Total </span>
                        <span data-testid="payroll-total">{total}</span>
                      </td>
                      <td colSpan={2} className="py-3 text-xs text-ink-soft">
                        <TravelVisits count={travelVisits} testId="payroll-total-travel" />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </PageShell>
  );
}
